/**
 * Studio Skills hooks (Wave 3 b73) — tenant CRUD against /studio-skills.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export interface StudioSkillVariable {
  name: string;
  type: 'string' | 'number' | 'boolean';
  description: string;
  required?: boolean;
}

export type StudioSkillScope = 'tenant' | 'system';

export interface StudioSkill {
  id: string;
  /**
   * Studio Phase 3E discriminator — 'tenant' for studio_skills rows,
   * 'system' for system_skills rows surfaced read-only to the tenant.
   */
  scope: StudioSkillScope;
  organizationId: string | null;
  createdBy: string | null;
  name: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies: string[];
  variables: StudioSkillVariable[];
  category: string;
  isActive: boolean;
  /** Q3 — set when the row was cloned from a system skill. */
  clonedFromSystemId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StudioSkillsListResponse {
  items: StudioSkill[];
  total: number;
}

const KEYS = {
  list: ['studio-skills'] as const,
  detail: (id: string) => ['studio-skills', id] as const,
};

export function useStudioSkills(opts: {
  activeOnly?: boolean;
  category?: string;
  scope?: 'tenant' | 'system' | 'all';
} = {}) {
  const params = new URLSearchParams();
  if (opts.activeOnly) params.set('activeOnly', 'true');
  if (opts.category) params.set('category', opts.category);
  if (opts.scope) params.set('scope', opts.scope);
  const qs = params.toString();

  return useQuery<StudioSkillsListResponse, Error>({
    queryKey: [...KEYS.list, opts],
    queryFn: () => apiClient.get<StudioSkillsListResponse>(`/studio-skills${qs ? `?${qs}` : ''}`),
  });
}

/**
 * Q3 clone flow — POST /studio-skills/clone-from-system/:systemId.
 * The optional `name` overrides the auto-generated tenant-local slug.
 */
export function useCloneStudioSkillFromSystem() {
  const qc = useQueryClient();
  return useMutation<{ data: StudioSkill }, Error, { systemId: string; name?: string }>({
    mutationFn: ({ systemId, name }) =>
      apiClient.post<{ data: StudioSkill }>(
        `/studio-skills/clone-from-system/${systemId}`,
        name ? { name } : {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export function useStudioSkillDetail(id: string | null | undefined) {
  return useQuery<{ data: StudioSkill }, Error>({
    queryKey: id ? KEYS.detail(id) : ['studio-skills', '__none__'],
    queryFn: () => apiClient.get<{ data: StudioSkill }>(`/studio-skills/${id}`),
    enabled: !!id,
  });
}

export interface CreateStudioSkillInput {
  name: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies?: string[];
  variables?: StudioSkillVariable[];
  category?: string;
  isActive?: boolean;
}

export function useCreateStudioSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: StudioSkill }, Error, CreateStudioSkillInput>({
    mutationFn: (input) => apiClient.post<{ data: StudioSkill }>('/studio-skills', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export interface UpdateStudioSkillInput extends Partial<CreateStudioSkillInput> {
  id: string;
}

export function useUpdateStudioSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: StudioSkill }, Error, UpdateStudioSkillInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: StudioSkill }>(`/studio-skills/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteStudioSkill() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete(`/studio-skills/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}
